/**
 * Schweizer Geografie-Referenzdatenbank für präzise Fluganalysen
 * Enthält verifizierte Koordinaten für markante Gipfel, Gebirgspässe, Täler und Ortschaften in den Schweizer Alpen und im Jura.
 */

export interface GeoLandmark {
  name: string;
  lat: number;
  lng: number;
  altM?: number;
  type: 'peak' | 'pass' | 'valley' | 'town' | 'ridge' | 'takeoff' | 'landing';
  canton?: string;
}

// Umfangreiche Datenbank der wichtigsten flugrelevanten Landmarken, Berge, Pässe, Start- und Landeplätze der Schweiz
export const SWISS_LANDMARKS: GeoLandmark[] = [
  // === WALLIS (ALETSCH, RHONETAL, BIETSCHHORN, MATTERTAL, GOMS) ===
  // Start- & Landeplätze Wallis
  { name: 'Fiescheralp (Kühboden)', lat: 46.412, lng: 8.103, altM: 2212, type: 'takeoff', canton: 'VS' },
  { name: 'Riederalp (Hohfluh)', lat: 46.386, lng: 8.024, altM: 2227, type: 'takeoff', canton: 'VS' },
  { name: 'Belalp', lat: 46.382, lng: 7.978, altM: 2137, type: 'takeoff', canton: 'VS' },
  { name: 'Landeplatz Fiesch', lat: 46.3986, lng: 8.1345, altM: 1055, type: 'landing', canton: 'VS' },
  { name: 'Landeplatz Bitsch', lat: 46.3421, lng: 8.0125, altM: 685, type: 'landing', canton: 'VS' },
  { name: 'Landeplatz Brig (Geschina)', lat: 46.3115, lng: 7.9815, altM: 690, type: 'landing', canton: 'VS' },
  { name: 'Flugplatz Raron', lat: 46.3039, lng: 7.8228, altM: 638, type: 'landing', canton: 'VS' },
  { name: 'Landeplatz Steg / Gampel', lat: 46.3135, lng: 7.7650, altM: 635, type: 'landing', canton: 'VS' },
  { name: 'Landeplatz Kippel (Lötschental)', lat: 46.3970, lng: 7.7645, altM: 1370, type: 'landing', canton: 'VS' },

  // Orte & Täler Wallis
  { name: 'Riederalp', lat: 46.380, lng: 8.033, altM: 1925, type: 'town', canton: 'VS' },
  { name: 'Bettmeralp', lat: 46.389, lng: 8.064, altM: 1957, type: 'town', canton: 'VS' },
  { name: 'Fiesch', lat: 46.401, lng: 8.133, altM: 1049, type: 'town', canton: 'VS' },
  { name: 'Naters', lat: 46.326, lng: 7.989, altM: 673, type: 'town', canton: 'VS' },
  { name: 'Brig / Glis', lat: 46.315, lng: 7.987, altM: 681, type: 'town', canton: 'VS' },
  { name: 'Visp', lat: 46.293, lng: 7.882, altM: 658, type: 'town', canton: 'VS' },
  { name: 'Ausserberg', lat: 46.315, lng: 7.848, altM: 1008, type: 'town', canton: 'VS' },
  { name: 'Baltschieder', lat: 46.305, lng: 7.873, altM: 658, type: 'town', canton: 'VS' },
  { name: 'Raron', lat: 46.310, lng: 7.802, altM: 638, type: 'town', canton: 'VS' },
  { name: 'Goppenstein', lat: 46.368, lng: 7.755, altM: 1216, type: 'town', canton: 'VS' },
  { name: 'Ferden', lat: 46.389, lng: 7.753, altM: 1375, type: 'town', canton: 'VS' },
  { name: 'Kippel', lat: 46.398, lng: 7.767, altM: 1376, type: 'town', canton: 'VS' },
  { name: 'Wiler (Lötschen)', lat: 46.404, lng: 7.785, altM: 1421, type: 'town', canton: 'VS' },
  { name: 'Blatten (Lötschental)', lat: 46.422, lng: 7.820, altM: 1540, type: 'town', canton: 'VS' },
  { name: 'Lötschental', lat: 46.405, lng: 7.780, altM: 1400, type: 'valley', canton: 'VS' },
  { name: 'Leukerbad', lat: 46.383, lng: 7.629, altM: 1402, type: 'town', canton: 'VS' },
  { name: 'Sierre', lat: 46.291, lng: 7.536, altM: 533, type: 'town', canton: 'VS' },
  { name: 'Sion', lat: 46.233, lng: 7.360, altM: 515, type: 'town', canton: 'VS' },
  { name: 'Crans-Montana', lat: 46.312, lng: 7.481, altM: 1500, type: 'town', canton: 'VS' },
  { name: 'Zermatt', lat: 45.976, lng: 7.749, altM: 1608, type: 'town', canton: 'VS' },
  { name: 'Saas-Fee', lat: 46.108, lng: 7.929, altM: 1800, type: 'town', canton: 'VS' },
  { name: 'Goms (Münster)', lat: 46.486, lng: 8.266, altM: 1370, type: 'valley', canton: 'VS' },

  // Berge & Pässe Wallis & Aletsch
  { name: 'Eggishorn', lat: 46.425, lng: 8.093, altM: 2926, type: 'peak', canton: 'VS' },
  { name: 'Bettmerhorn', lat: 46.417, lng: 8.064, altM: 2858, type: 'peak', canton: 'VS' },
  { name: 'Grosser Aletschgletscher', lat: 46.485, lng: 8.055, altM: 2800, type: 'valley', canton: 'VS' },
  { name: 'Aletschhorn', lat: 46.465, lng: 7.990, altM: 4193, type: 'peak', canton: 'VS' },
  { name: 'Nesthorn', lat: 46.411, lng: 7.925, altM: 3822, type: 'peak', canton: 'VS' },
  { name: 'Bietschhorn', lat: 46.391, lng: 7.851, altM: 3934, type: 'peak', canton: 'VS' },
  { name: 'Bietschhorn-Südwand', lat: 46.378, lng: 7.848, altM: 2848, type: 'ridge', canton: 'VS' },
  { name: 'Hogleifa', lat: 46.375, lng: 7.817, altM: 2780, type: 'peak', canton: 'VS' },
  { name: 'Wilerhorn (Lötschen)', lat: 46.422, lng: 7.771, altM: 3307, type: 'peak', canton: 'VS' },
  { name: 'Hockenhorn', lat: 46.429, lng: 7.747, altM: 3293, type: 'peak', canton: 'VS/BE' },
  { name: 'Lötschenpass', lat: 46.418, lng: 7.721, altM: 2690, type: 'pass', canton: 'VS/BE' },
  { name: 'Balmhorn', lat: 46.426, lng: 7.693, altM: 3698, type: 'peak', canton: 'VS/BE' },
  { name: 'Altels', lat: 46.442, lng: 7.677, altM: 3629, type: 'peak', canton: 'VS/BE' },
  { name: 'Rinderhorn', lat: 46.412, lng: 7.654, altM: 3448, type: 'peak', canton: 'VS' },
  { name: 'Gemmipass', lat: 46.405, lng: 7.616, altM: 2314, type: 'pass', canton: 'VS/BE' },
  { name: 'Torrenthorn', lat: 46.371, lng: 7.682, altM: 2998, type: 'peak', canton: 'VS' },
  { name: 'Wildstrubel', lat: 46.401, lng: 7.528, altM: 3244, type: 'peak', canton: 'VS/BE' },
  { name: 'Matterhorn', lat: 45.976, lng: 7.658, altM: 4478, type: 'peak', canton: 'VS' },
  { name: 'Weisshorn', lat: 46.106, lng: 7.716, altM: 4505, type: 'peak', canton: 'VS' },
  { name: 'Grimselpass', lat: 46.561, lng: 8.337, altM: 2164, type: 'pass', canton: 'BE/VS' },
  { name: 'Furkapass', lat: 46.572, lng: 8.415, altM: 2429, type: 'pass', canton: 'VS/UR' },

  // === BERNER OBERLAND (KANDER-/KIENTAL, INTERLAKEN, JUNGFRAU) ===
  // Start- & Landeplätze Berner Oberland
  { name: 'Landeplatz Lehn (Unterseen)', lat: 46.6808, lng: 7.8236, altM: 560, type: 'landing', canton: 'BE' },
  { name: 'Landeplatz Höhematte (Interlaken)', lat: 46.6872, lng: 7.8631, altM: 568, type: 'landing', canton: 'BE' },
  { name: 'Flugplatz Frutigen', lat: 46.5920, lng: 7.6515, altM: 775, type: 'landing', canton: 'BE' },
  { name: 'Flugplatz Reichenbach', lat: 46.6110, lng: 7.6795, altM: 708, type: 'landing', canton: 'BE' },
  { name: 'Landeplatz Kandersteg', lat: 46.4910, lng: 7.6710, altM: 1175, type: 'landing', canton: 'BE' },
  { name: 'Landeplatz Grindelwald (Grund/Bodmi)', lat: 46.6210, lng: 8.0280, altM: 950, type: 'landing', canton: 'BE' },
  { name: 'Landeplatz Stechelberg', lat: 46.5490, lng: 7.9015, altM: 865, type: 'landing', canton: 'BE' },
  { name: 'Landeplatz Zweisimmen', lat: 46.5510, lng: 7.3790, altM: 940, type: 'landing', canton: 'BE' },
  { name: 'Startplatz Niesen', lat: 46.6455, lng: 7.6510, altM: 2330, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz Niederhorn', lat: 46.7110, lng: 7.7770, altM: 1950, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz Amisbühl (Beatenberg)', lat: 46.7020, lng: 7.7880, altM: 1300, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz First (Grindelwald)', lat: 46.6600, lng: 8.0540, altM: 2168, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz Schynige Platte', lat: 46.6530, lng: 7.9080, altM: 2076, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz Allmenalp (Kandersteg)', lat: 46.4920, lng: 7.6540, altM: 1725, type: 'takeoff', canton: 'BE' },
  { name: 'Startplatz Elsigenalp', lat: 46.5280, lng: 7.6250, altM: 1800, type: 'takeoff', canton: 'BE' },

  // Orte & Täler Berner Oberland
  { name: 'Kandersteg', lat: 46.495, lng: 7.674, altM: 1174, type: 'town', canton: 'BE' },
  { name: 'Gasterntal', lat: 46.460, lng: 7.700, altM: 1350, type: 'valley', canton: 'BE' },
  { name: 'Blausee-Mitholz', lat: 46.523, lng: 7.668, altM: 900, type: 'town', canton: 'BE' },
  { name: 'Kandergrund', lat: 46.541, lng: 7.666, altM: 858, type: 'town', canton: 'BE' },
  { name: 'Frutigen', lat: 46.588, lng: 7.648, altM: 779, type: 'town', canton: 'BE' },
  { name: 'Reichenbach im Kandertal', lat: 46.626, lng: 7.694, altM: 706, type: 'town', canton: 'BE' },
  { name: 'Kiental (Dorf)', lat: 46.592, lng: 7.721, altM: 960, type: 'town', canton: 'BE' },
  { name: 'Mülenen', lat: 46.639, lng: 7.688, altM: 692, type: 'town', canton: 'BE' },
  { name: 'Aeschi bei Spiez', lat: 46.654, lng: 7.697, altM: 860, type: 'town', canton: 'BE' },
  { name: 'Spiez', lat: 46.686, lng: 7.677, altM: 607, type: 'town', canton: 'BE' },
  { name: 'Interlaken / Bödeli', lat: 46.686, lng: 7.863, altM: 568, type: 'town', canton: 'BE' },
  { name: 'Unterseen', lat: 46.683, lng: 7.845, altM: 565, type: 'town', canton: 'BE' },
  { name: 'Lauterbrunnen', lat: 46.593, lng: 7.909, altM: 795, type: 'town', canton: 'BE' },
  { name: 'Mürren', lat: 46.559, lng: 7.892, altM: 1638, type: 'town', canton: 'BE' },
  { name: 'Wengen', lat: 46.605, lng: 7.922, altM: 1274, type: 'town', canton: 'BE' },
  { name: 'Grindelwald', lat: 46.624, lng: 8.041, altM: 1034, type: 'town', canton: 'BE' },
  { name: 'Meiringen / Haslital', lat: 46.726, lng: 8.188, altM: 595, type: 'town', canton: 'BE' },
  { name: 'Brienz', lat: 46.755, lng: 8.038, altM: 566, type: 'town', canton: 'BE' },
  { name: 'Adelboden', lat: 46.497, lng: 7.559, altM: 1350, type: 'town', canton: 'BE' },
  { name: 'Lenk im Simmental', lat: 46.458, lng: 7.443, altM: 1068, type: 'town', canton: 'BE' },
  { name: 'Zweisimmen', lat: 46.554, lng: 7.375, altM: 941, type: 'town', canton: 'BE' },

  // Berge, Grate & Pässe Berner Oberland
  { name: 'Doldenhorn', lat: 46.468, lng: 7.712, altM: 3643, type: 'peak', canton: 'BE' },
  { name: 'Blüemlisalphorn', lat: 46.488, lng: 7.778, altM: 3661, type: 'peak', canton: 'BE' },
  { name: 'Oeschinen Aussichtspunkt', lat: 46.498, lng: 7.695, altM: 1950, type: 'ridge', canton: 'BE' },
  { name: 'Oeschinensee', lat: 46.498, lng: 7.728, altM: 1578, type: 'valley', canton: 'BE' },
  { name: 'Dündenhorn', lat: 46.545, lng: 7.729, altM: 2860, type: 'peak', canton: 'BE' },
  { name: 'Ober Giesene / Ärmighorn-Südflanke', lat: 46.535, lng: 7.705, altM: 2150, type: 'ridge', canton: 'BE' },
  { name: 'Ärmighorn', lat: 46.558, lng: 7.712, altM: 2742, type: 'peak', canton: 'BE' },
  { name: 'Gehrihorn', lat: 46.575, lng: 7.702, altM: 2130, type: 'peak', canton: 'BE' },
  { name: 'Sunnbüel', lat: 46.452, lng: 7.643, altM: 1936, type: 'ridge', canton: 'BE' },
  { name: 'Elsighorn', lat: 46.536, lng: 7.635, altM: 2341, type: 'peak', canton: 'BE' },
  { name: 'Stand (Elsigenalp)', lat: 46.525, lng: 7.620, altM: 2320, type: 'peak', canton: 'BE' },
  { name: 'Dreispitz', lat: 46.592, lng: 7.733, altM: 2520, type: 'peak', canton: 'BE' },
  { name: 'Bire (Kiental)', lat: 46.568, lng: 7.738, altM: 2503, type: 'peak', canton: 'BE' },
  { name: 'Sulegg', lat: 46.611, lng: 7.791, altM: 2413, type: 'peak', canton: 'BE' },
  { name: 'Schwalmere', lat: 46.588, lng: 7.822, altM: 2775, type: 'peak', canton: 'BE' },
  { name: 'Morgenberghorn', lat: 46.624, lng: 7.777, altM: 2249, type: 'peak', canton: 'BE' },
  { name: 'Abendberg', lat: 46.662, lng: 7.828, altM: 1275, type: 'ridge', canton: 'BE' },
  { name: 'Niesen', lat: 46.645, lng: 7.651, altM: 2362, type: 'peak', canton: 'BE' },
  { name: 'Stockhorn', lat: 46.694, lng: 7.537, altM: 2190, type: 'peak', canton: 'BE' },
  { name: 'Niederhorn', lat: 46.711, lng: 7.777, altM: 1963, type: 'peak', canton: 'BE' },
  { name: 'Sigriswiler Rothorn', lat: 46.735, lng: 7.789, altM: 2051, type: 'peak', canton: 'BE' },
  { name: 'Hohgant', lat: 46.786, lng: 7.904, altM: 2197, type: 'peak', canton: 'BE' },
  { name: 'Brienzer Rothorn', lat: 46.787, lng: 8.046, altM: 2350, type: 'peak', canton: 'BE/LU/OW' },
  { name: 'Schilthorn', lat: 46.559, lng: 7.893, altM: 2970, type: 'peak', canton: 'BE' },
  { name: 'Männlichen', lat: 46.613, lng: 7.941, altM: 2343, type: 'peak', canton: 'BE' },
  { name: 'Faulhorn', lat: 46.674, lng: 7.999, altM: 2681, type: 'peak', canton: 'BE' },
  { name: 'Jungfrau', lat: 46.536, lng: 7.962, altM: 4158, type: 'peak', canton: 'BE/VS' },
  { name: 'Mönch', lat: 46.558, lng: 7.998, altM: 4107, type: 'peak', canton: 'BE/VS' },
  { name: 'Eiger', lat: 46.577, lng: 8.005, altM: 3967, type: 'peak', canton: 'BE' },
  { name: 'Grosse Scheidegg', lat: 46.656, lng: 8.102, altM: 1962, type: 'pass', canton: 'BE' },
  { name: 'Kleine Scheidegg', lat: 46.585, lng: 7.961, altM: 2061, type: 'pass', canton: 'BE' },

  // === ZENTRALSCHWEIZ & ENTLEBUCH ===
  { name: 'Landeplatz Schüpfheim', lat: 46.953, lng: 8.018, altM: 720, type: 'landing', canton: 'LU' },
  { name: 'Landeplatz Engelberg (Sportplatz)', lat: 46.818, lng: 8.402, altM: 1005, type: 'landing', canton: 'OW' },
  { name: 'Landeplatz Wolfenschiessen', lat: 46.906, lng: 8.391, altM: 510, type: 'landing', canton: 'NW' },
  { name: 'Landeplatz Dallenwil', lat: 46.932, lng: 8.388, altM: 485, type: 'landing', canton: 'NW' },
  { name: 'Landeplatz Buochs / Ennetbürgen', lat: 46.980, lng: 8.435, altM: 440, type: 'landing', canton: 'NW' },
  { name: 'Schüpfheim', lat: 46.950, lng: 8.023, altM: 719, type: 'town', canton: 'LU' },
  { name: 'Hasle (Entlebuch)', lat: 46.979, lng: 8.055, altM: 711, type: 'town', canton: 'LU' },
  { name: 'Entlebuch (Dorf)', lat: 46.992, lng: 8.063, altM: 684, type: 'town', canton: 'LU' },
  { name: 'Flühli', lat: 46.883, lng: 8.017, altM: 884, type: 'town', canton: 'LU' },
  { name: 'Sörenberg', lat: 46.824, lng: 8.032, altM: 1159, type: 'town', canton: 'LU' },
  { name: 'Escholzmatt', lat: 46.914, lng: 7.933, altM: 852, type: 'town', canton: 'LU' },
  { name: 'Marbach (Entlebuch)', lat: 46.855, lng: 7.899, altM: 871, type: 'town', canton: 'LU' },
  { name: 'Schrattenfluh (Hächlen)', lat: 46.839, lng: 7.971, altM: 2093, type: 'peak', canton: 'LU' },
  { name: 'Fürstein', lat: 46.903, lng: 8.070, altM: 2040, type: 'peak', canton: 'LU/OW' },
  { name: 'Glaubenbielenpass', lat: 46.826, lng: 8.096, altM: 1611, type: 'pass', canton: 'OW/LU' },
  { name: 'Glaubenbergpass', lat: 46.892, lng: 8.106, altM: 1543, type: 'pass', canton: 'OW/LU' },
  { name: 'Napf', lat: 47.006, lng: 7.940, altM: 1408, type: 'peak', canton: 'BE/LU' },
  { name: 'Sarnen', lat: 46.896, lng: 8.245, altM: 473, type: 'town', canton: 'OW' },
  { name: 'Giswil', lat: 46.833, lng: 8.183, altM: 485, type: 'town', canton: 'OW' },
  { name: 'Lungern', lat: 46.786, lng: 8.159, altM: 712, type: 'town', canton: 'OW' },
  { name: 'Brünigpass', lat: 46.757, lng: 8.136, altM: 1008, type: 'pass', canton: 'BE/OW' },
  { name: 'Pilatus (Tomlishorn)', lat: 46.980, lng: 8.246, altM: 2128, type: 'peak', canton: 'OW/NW/LU' },
  { name: 'Stanserhorn', lat: 46.929, lng: 8.340, altM: 1898, type: 'peak', canton: 'NW' },
  { name: 'Stans', lat: 46.958, lng: 8.366, altM: 452, type: 'town', canton: 'NW' },
  { name: 'Engelberg', lat: 46.820, lng: 8.404, altM: 1000, type: 'town', canton: 'OW' },
  { name: 'Titlis', lat: 46.772, lng: 8.438, altM: 3238, type: 'peak', canton: 'OW/BE/UR' },
  { name: 'Buochserhorn', lat: 46.946, lng: 8.423, altM: 1807, type: 'peak', canton: 'NW' },
  { name: 'Rigi Kulm', lat: 47.056, lng: 8.485, altM: 1797, type: 'peak', canton: 'SZ' },
  { name: 'Grosser Mythen', lat: 47.029, lng: 8.689, altM: 1898, type: 'peak', canton: 'SZ' },
  { name: 'Luzern', lat: 47.050, lng: 8.310, altM: 436, type: 'town', canton: 'LU' },
  { name: 'Alpnach', lat: 46.940, lng: 8.272, altM: 464, type: 'town', canton: 'OW' },

  // === GRAUBÜNDEN, URI & TESSIN ===
  { name: 'Landeplatz Flims', lat: 46.825, lng: 9.288, altM: 1080, type: 'landing', canton: 'GR' },
  { name: 'Landeplatz Ilanz', lat: 46.775, lng: 9.201, altM: 698, type: 'landing', canton: 'GR' },
  { name: 'Landeplatz Davos (Klosters Platz)', lat: 46.785, lng: 9.832, altM: 1530, type: 'landing', canton: 'GR' },
  { name: 'Landeplatz Samedan', lat: 46.533, lng: 9.878, altM: 1705, type: 'landing', canton: 'GR' },
  { name: 'Andermatt / Urserental', lat: 46.633, lng: 8.594, altM: 1447, type: 'town', canton: 'UR' },
  { name: 'Disentis / Mustér', lat: 46.710, lng: 8.853, altM: 1130, type: 'town', canton: 'GR' },
  { name: 'Oberalppass', lat: 46.659, lng: 8.671, altM: 2044, type: 'pass', canton: 'UR/GR' },
  { name: 'Flims / Laax', lat: 46.836, lng: 9.284, altM: 1100, type: 'town', canton: 'GR' },
  { name: 'Crap Sogn Gion', lat: 46.835, lng: 9.215, altM: 2216, type: 'peak', canton: 'GR' },
  { name: 'Chur', lat: 46.851, lng: 9.532, altM: 593, type: 'town', canton: 'GR' },
  { name: 'Davos', lat: 46.800, lng: 9.836, altM: 1560, type: 'town', canton: 'GR' },
  { name: 'Klosters', lat: 46.870, lng: 9.880, altM: 1194, type: 'town', canton: 'GR' },
  { name: 'St. Moritz / Oberengadin', lat: 46.498, lng: 9.839, altM: 1822, type: 'town', canton: 'GR' },
  { name: 'Malojapass', lat: 46.400, lng: 9.697, altM: 1815, type: 'pass', canton: 'GR' },
  { name: 'Piz Bernina', lat: 46.382, lng: 9.908, altM: 4049, type: 'peak', canton: 'GR' },
  { name: 'Monte Tamaro', lat: 46.104, lng: 8.866, altM: 1962, type: 'peak', canton: 'TI' },
  { name: 'Monte Lema', lat: 46.024, lng: 8.829, altM: 1621, type: 'peak', canton: 'TI' },
  { name: 'Bellinzona', lat: 46.195, lng: 9.023, altM: 238, type: 'town', canton: 'TI' },
  { name: 'Locarno / Cardada', lat: 46.199, lng: 8.799, altM: 1340, type: 'town', canton: 'TI' },

  // === OSTSCHWEIZ & JURA ===
  { name: 'Landeplatz Wasserauen', lat: 47.284, lng: 9.432, altM: 868, type: 'landing', canton: 'AI' },
  { name: 'Säntis', lat: 47.249, lng: 9.343, altM: 2502, type: 'peak', canton: 'AR/AI/SG' },
  { name: 'Ebenalp', lat: 47.283, lng: 9.412, altM: 1640, type: 'peak', canton: 'AI' },
  { name: 'Kronberg', lat: 47.298, lng: 9.329, altM: 1663, type: 'peak', canton: 'AI' },
  { name: 'Hoher Kasten', lat: 47.284, lng: 9.484, altM: 1795, type: 'peak', canton: 'AI/SG' },
  { name: 'Chäserrugg / Churfirsten', lat: 47.155, lng: 9.313, altM: 2262, type: 'peak', canton: 'SG' },
  { name: 'Chasseral', lat: 47.132, lng: 7.060, altM: 1606, type: 'peak', canton: 'BE' },
  { name: 'Weissenstein', lat: 47.253, lng: 7.509, altM: 1395, type: 'peak', canton: 'SO' }
];

export function calcDistKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371.0;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Ermittelt den nächstgelegenen Schweizer Ort / Gemeinde für beliebige Koordinaten.
 */
export function resolveSwissPlace(lat: number, lng: number): {
  name: string;
  distKm: number;
  canton?: string;
  display: string;
} {
  let closest: GeoLandmark | null = null;
  let minDist = Infinity;

  // Suche bevorzugt nach Towns & Valleys
  const towns = SWISS_LANDMARKS.filter(l => l.type === 'town' || l.type === 'valley');
  for (const t of towns) {
    const d = calcDistKm(lat, lng, t.lat, t.lng);
    if (d < minDist) {
      minDist = d;
      closest = t;
    }
  }

  if (!closest) {
    return {
      name: `Position [${lat.toFixed(3)}, ${lng.toFixed(3)}]`,
      distKm: 0,
      display: `Position [${lat.toFixed(3)}, ${lng.toFixed(3)}]`
    };
  }

  const cantonSuffix = closest.canton ? ` (${closest.canton})` : '';
  const display = minDist <= 2.5
    ? `${closest.name}${cantonSuffix}`
    : `${closest.name}${cantonSuffix} (ca. ${minDist.toFixed(1)} km entfernt)`;

  return {
    name: closest.name,
    distKm: Number(minDist.toFixed(1)),
    canton: closest.canton,
    display
  };
}

/**
 * Ermittelt hochpräzise den Startort (offizieller Startplatz oder Berggipfel/Ortschaft).
 */
export function resolveTakeoffLocation(lat: number, lng: number, altM?: number): {
  name: string;
  isOfficialTakeoff: boolean;
  distKm: number;
  display: string;
  lat: number;
  lng: number;
  altitudeM?: number;
} {
  // 1. Suche nach offiziellen Startplätzen (Radius bis 1.2 km)
  const takeoffs = SWISS_LANDMARKS.filter(l => l.type === 'takeoff');
  let bestTakeoff: GeoLandmark | null = null;
  let minTakeoffDist = Infinity;

  for (const to of takeoffs) {
    const d = calcDistKm(lat, lng, to.lat, to.lng);
    if (d <= 1.2 && d < minTakeoffDist) {
      minTakeoffDist = d;
      bestTakeoff = to;
    }
  }

  if (bestTakeoff) {
    return {
      name: bestTakeoff.name,
      isOfficialTakeoff: true,
      distKm: Number(minTakeoffDist.toFixed(1)),
      display: `Offizieller Startplatz ${bestTakeoff.name}`,
      lat: bestTakeoff.lat,
      lng: bestTakeoff.lng,
      altitudeM: altM || bestTakeoff.altM
    };
  }

  // 2. Fallback auf nächsten Berg oder Ort
  const generalPlaces = SWISS_LANDMARKS.filter(l => l.type === 'peak' || l.type === 'ridge' || l.type === 'town');
  let closestPlace: GeoLandmark | null = null;
  let minPlaceDist = Infinity;

  for (const p of generalPlaces) {
    const d = calcDistKm(lat, lng, p.lat, p.lng);
    if (d < minPlaceDist) {
      minPlaceDist = d;
      closestPlace = p;
    }
  }

  const name = closestPlace ? closestPlace.name : `Start [${lat.toFixed(3)}, ${lng.toFixed(3)}]`;
  return {
    name,
    isOfficialTakeoff: false,
    distKm: Number(minPlaceDist.toFixed(1)),
    display: minPlaceDist <= 2.0 ? `Start bei ${name}` : `Start bei ${name} (ca. ${minPlaceDist.toFixed(1)} km entfernt)`,
    lat,
    lng,
    altitudeM: altM || closestPlace?.altM
  };
}

/**
 * Ermittelt hochpräzise den Landeort:
 * Unterscheidet strikt zwischen offiziellem Landeplatz (Distanz <= 700m) und Außenlandung!
 */
export function resolveLandingLocation(lat: number, lng: number, altM?: number): {
  name: string;
  isOfficialLanding: boolean;
  distKm: number;
  display: string;
  lat: number;
  lng: number;
  altitudeM?: number;
} {
  // 1. Suche nach offiziellen Landeplätzen (Radius bis 750m)
  const officialLandings = SWISS_LANDMARKS.filter(l => l.type === 'landing');
  let bestLanding: GeoLandmark | null = null;
  let minLandingDist = Infinity;

  for (const l of officialLandings) {
    const d = calcDistKm(lat, lng, l.lat, l.lng);
    if (d <= 0.75 && d < minLandingDist) {
      minLandingDist = d;
      bestLanding = l;
    }
  }

  if (bestLanding) {
    return {
      name: bestLanding.name,
      isOfficialLanding: true,
      distKm: Number(minLandingDist.toFixed(1)),
      display: bestLanding.name,
      lat: bestLanding.lat,
      lng: bestLanding.lng,
      altitudeM: altM || bestLanding.altM
    };
  }

  // 2. Wenn kein offizieller Landeplatz im 750m-Radius: Eindeutige Aussenlandung!
  const towns = SWISS_LANDMARKS.filter(l => l.type === 'town' || l.type === 'valley');
  let closestTown: GeoLandmark | null = null;
  let minTownDist = Infinity;

  for (const t of towns) {
    const d = calcDistKm(lat, lng, t.lat, t.lng);
    if (d < minTownDist) {
      minTownDist = d;
      closestTown = t;
    }
  }

  const townName = closestTown ? closestTown.name : `Koordinate [${lat.toFixed(3)}, ${lng.toFixed(3)}]`;
  const distStr = minTownDist > 0.5 ? ` (ca. ${minTownDist.toFixed(1)} km von ${townName})` : ` bei ${townName}`;
  const outlandingName = `Außenlandung${distStr}`;

  return {
    name: outlandingName,
    isOfficialLanding: false,
    distKm: Number(minTownDist.toFixed(1)),
    display: `${outlandingName}${closestTown?.canton ? ` (${closestTown.canton})` : ''}`,
    lat,
    lng,
    altitudeM: altM || closestTown?.altM
  };
}

/**
 * Erstellt eine präzise chronologische Landmark-Sequenz entlang der überflogenen Flugroute.
 * Tastet mit hoher Dichte ab, berechnet den Closest Point of Approach (CPA) für jeden Berg/Pass/Ort,
 * speichert die echte Überflughöhe und ordnet die Wegpunkte exakt in Flugrichtung.
 */
export function extractTrackLandmarks(
  fixes: Array<{ lat: number; lng: number; altM?: number; alt?: number; time?: any; timestamp?: any }>,
  maxLandmarks: number = 25
): Array<{
  name: string;
  type: string;
  altM: number;
  distKm: number;
  lat: number;
  lng: number;
  fixIndex: number;
  description: string;
}> {
  if (!fixes || fixes.length < 5) return [];

  // Hohe Abtastdichte: bis zu 250 Stützpunkte für lückenlose Bergenerkennung
  const step = Math.max(1, Math.floor(fixes.length / 250));
  const sampledWithIndex: Array<{ fix: any; origIndex: number }> = [];
  for (let i = 0; i < fixes.length; i += step) {
    sampledWithIndex.push({ fix: fixes[i], origIndex: i });
  }

  // Für jedes flugrelevante Landmark in der Schweiz den geringsten Abstand (CPA) zum Flugweg ermitteln
  interface LandmarkMatch {
    landmark: GeoLandmark;
    minDist: number;
    altM: number;
    fixIndex: number;
  }

  const candidateMatches: LandmarkMatch[] = [];

  // Relevante Landmarken filtern (Peaks, Passes, Ridges, markante Valleys)
  const relevantLandmarks = SWISS_LANDMARKS.filter(l => l.type === 'peak' || l.type === 'pass' || l.type === 'ridge' || l.type === 'valley');

  for (const lm of relevantLandmarks) {
    let closestDist = Infinity;
    let closestAlt = 0;
    let closestIndex = -1;

    // Erlaubter Erfassungsradius je nach Landmark-Typ:
    // WICHTIG: Streng limitiert auf echten Flugnahbereich (max 1.8 km für Gipfel),
    // um NIEMALS fälschlicherweise Berge der gegenüberliegenden Talseite (wie Elsighorn auf 3.8 km) zu erfassen!
    const maxRadius = lm.type === 'peak' ? 1.8 : lm.type === 'ridge' ? 1.5 : lm.type === 'pass' ? 1.5 : 1.2;

    for (const item of sampledWithIndex) {
      const f = item.fix;
      const fLat = f.lat ?? f.latitude ?? 0;
      const fLng = f.lng ?? f.longitude ?? 0;
      const d = calcDistKm(fLat, fLng, lm.lat, lm.lng);

      if (d < closestDist) {
        closestDist = d;
        closestAlt = f.altM || f.alt || f.gpsAltitude || f.pressureAltitude || 0;
        closestIndex = item.origIndex;
      }
    }

    if (closestDist <= maxRadius && closestIndex >= 0) {
      candidateMatches.push({
        landmark: lm,
        minDist: closestDist,
        altM: Math.round(closestAlt),
        fixIndex: closestIndex
      });
    }
  }

  // Chronologisch nach Flugzeit / Track-Fortschritt sortieren
  candidateMatches.sort((a, b) => a.fixIndex - b.fixIndex);

  // Duplikate und eng beieinander liegende Landmarken (< 1.8 km) entprellen
  const finalLandmarks: Array<{
    name: string;
    type: string;
    altM: number;
    distKm: number;
    lat: number;
    lng: number;
    fixIndex: number;
    description: string;
  }> = [];

  const seenNames = new Set<string>();

  for (const match of candidateMatches) {
    if (seenNames.has(match.landmark.name)) continue;

    // Abstand zum vorherigen Landmark prüfen
    if (finalLandmarks.length > 0) {
      const prev = finalLandmarks[finalLandmarks.length - 1];
      const distToPrev = calcDistKm(match.landmark.lat, match.landmark.lng, prev.lat, prev.lng);
      if (distToPrev < 1.8) {
        // Wenn das vorherige Landmark weiter weg war als das neue, ersetze es
        if (match.minDist < prev.distKm) {
          finalLandmarks.pop();
          seenNames.delete(prev.name);
        } else {
          continue;
        }
      }
    }

    seenNames.add(match.landmark.name);
    finalLandmarks.push({
      name: match.landmark.name,
      type: match.landmark.type,
      altM: match.altM,
      distKm: Number(match.minDist.toFixed(1)),
      lat: match.landmark.lat,
      lng: match.landmark.lng,
      fixIndex: match.fixIndex,
      description: `${match.landmark.name}${match.landmark.canton ? ` (${match.landmark.canton})` : ''} [${match.altM}m MSL, Abstand: ${match.minDist.toFixed(1)} km]`
    });

    if (finalLandmarks.length >= maxLandmarks) break;
  }

  return finalLandmarks;
}
